import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Box,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VpnKey as KeyIcon,
  CheckCircle as ActiveIcon,
  Cancel as SuspendedIcon,
  Pending as PendingIcon
} from '@mui/icons-material';
import { Tenant, CreateTenantRequest, UpdateTenantRequest } from '../types/index.ts';
import { adminApi } from '../services/api.ts';

interface TenantManagerProps {
  onLogout?: () => void;
}

const TenantManager: React.FC<TenantManagerProps> = ({ onLogout }) => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [formData, setFormData] = useState<CreateTenantRequest>({
    tenant_id: '',
    name: '',
    billing_tier: 'starter',
    contact_email: ''
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoading(true);
      const tenantList = await adminApi.getTenants();
      setTenants(tenantList);
    } catch (error) {
      showSnackbar('Failed to load tenants', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (tenant?: Tenant) => {
    if (tenant) {
      setEditingTenant(tenant);
      setFormData({
        tenant_id: tenant.tenant_id,
        name: tenant.name,
        billing_tier: tenant.billing_tier,
        contact_email: tenant.contact_email || ''
      });
    } else {
      setEditingTenant(null);
      setFormData({
        tenant_id: '',
        name: '',
        billing_tier: 'starter',
        contact_email: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTenant(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingTenant) {
        const updates: UpdateTenantRequest = {
          name: formData.name,
          billing_tier: formData.billing_tier,
          contact_email: formData.contact_email
        };
        await adminApi.updateTenant(editingTenant.tenant_id, updates);
        showSnackbar('Tenant updated successfully', 'success');
      } else {
        await adminApi.createTenant(formData);
        showSnackbar('Tenant created successfully', 'success');
      }
      handleCloseDialog();
      loadTenants();
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Operation failed', 'error');
    }
  };

  const handleDeleteTenant = async (tenantId: string) => {
    if (window.confirm('Are you sure you want to suspend this tenant?')) {
      try {
        await adminApi.deleteTenant(tenantId);
        showSnackbar('Tenant suspended successfully', 'success');
        loadTenants();
      } catch (error) {
        showSnackbar('Failed to suspend tenant', 'error');
      }
    }
  };

  const handleGenerateApiKey = async (tenantId: string) => {
    try {
      const result = await adminApi.generateApiKey(tenantId);
      // In a real app, you'd show this key to the user and warn them to save it
      alert(`API Key generated: ${result.api_key}\n\nPlease save this key securely. It will not be shown again.`);
      showSnackbar('API key generated successfully', 'success');
    } catch (error) {
      showSnackbar('Failed to generate API key', 'error');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <ActiveIcon color="success" />;
      case 'suspended':
        return <SuspendedIcon color="error" />;
      case 'pending':
        return <PendingIcon color="warning" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header with glass effect */}
      <Box
        sx={{
          background: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          p: 3,
          mb: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                background: 'linear-gradient(135deg, #4F46E5, #9333EA, #3B82F6)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 700,
                mb: 1,
              }}
            >
              Flux Admin Dashboard
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Multi-Tenant Observability Platform Management
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.8) 0%, rgba(147, 51, 234, 0.9) 50%, rgba(59, 130, 246, 0.8) 100%)',
              boxShadow: '0 8px 32px rgba(147, 51, 234, 0.2), 0 4px 16px rgba(59, 130, 246, 0.1)',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.9) 0%, rgba(147, 51, 234, 1.0) 50%, rgba(59, 130, 246, 0.9) 100%)',
                transform: 'scale(1.02)',
                boxShadow: '0 12px 40px rgba(147, 51, 234, 0.3), 0 6px 20px rgba(59, 130, 246, 0.2)',
              },
            }}
          >
            Add Tenant
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tenants Overview
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Total Tenants: {tenants.length} |
                Active: {tenants.filter(t => t.status === 'active').length} |
                Suspended: {tenants.filter(t => t.status === 'suspended').length}
              </Typography>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Tenant ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Billing Tier</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Contact Email</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          Loading tenants...
                        </TableCell>
                      </TableRow>
                    ) : tenants.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No tenants found. Create your first tenant to get started.
                        </TableCell>
                      </TableRow>
                    ) : (
                      tenants.map((tenant) => (
                        <TableRow key={tenant.tenant_id}>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {tenant.tenant_id}
                            </Typography>
                          </TableCell>
                          <TableCell>{tenant.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={tenant.billing_tier}
                              color={
                                tenant.billing_tier === 'enterprise' ? 'primary' :
                                tenant.billing_tier === 'professional' ? 'secondary' : 'default'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              {getStatusIcon(tenant.status)}
                              <Chip
                                label={tenant.status}
                                color={getStatusColor(tenant.status) as any}
                                size="small"
                              />
                            </Box>
                          </TableCell>
                          <TableCell>{tenant.contact_email || '-'}</TableCell>
                          <TableCell>
                            {new Date(tenant.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Generate API Key">
                              <IconButton
                                onClick={() => handleGenerateApiKey(tenant.tenant_id)}
                                color="primary"
                                size="small"
                              >
                                <KeyIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Tenant">
                              <IconButton
                                onClick={() => handleOpenDialog(tenant)}
                                color="primary"
                                size="small"
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Suspend Tenant">
                              <IconButton
                                onClick={() => handleDeleteTenant(tenant.tenant_id)}
                                color="error"
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create/Edit Tenant Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTenant ? 'Edit Tenant' : 'Create New Tenant'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Tenant ID"
            fullWidth
            variant="outlined"
            value={formData.tenant_id}
            onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
            disabled={!!editingTenant}
            helperText="Unique identifier (alphanumeric, hyphens, underscores only)"
          />
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Billing Tier</InputLabel>
            <Select
              value={formData.billing_tier}
              label="Billing Tier"
              onChange={(e) => setFormData({ ...formData, billing_tier: e.target.value as any })}
            >
              <MenuItem value="starter">Starter</MenuItem>
              <MenuItem value="professional">Professional</MenuItem>
              <MenuItem value="enterprise">Enterprise</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Contact Email"
            fullWidth
            variant="outlined"
            type="email"
            value={formData.contact_email}
            onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingTenant ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TenantManager;
